// Spec 078 — référentiels canoniques (contrat v1.4 §6, R4). Source de vérité = runbook ; le portail
// les utilise en confort UI **non bloquant** (mode défensif conservé : code brut si valeur hors annexe).

import type { MilestoneStatus } from '../types/audit'

// §6.1 — les 8 jalons (ordre · step · label). seq 0 « Démarrage » = non-milestone (à ne pas afficher).
export interface CanonicalJalon {
  seq: number
  step: string
  label: string
}

export const CANONICAL_JALONS: readonly CanonicalJalon[] = [
  { seq: 1, step: 'Init', label: 'Initialisation et connexion sécurisée' },
  { seq: 2, step: 'CollectLicenses', label: "Collecte des licences de l'organisation" },
  { seq: 3, step: 'CollectUsers', label: 'Collecte des licences utilisateurs' },
  { seq: 4, step: 'CollectActivity', label: "Collecte de l'activité et des usages" },
  { seq: 5, step: 'Analyze', label: 'Analyse et détection des optimisations' },
  { seq: 6, step: 'Simulate', label: "Simulation des scénarios d'économies" },
  { seq: 7, step: 'Report', label: 'Génération du rapport' },
  { seq: 8, step: 'Done', label: 'Audit terminé' },
]

export const CANONICAL_TOTAL = 8

// §4.4 — catalogue failureCode (8 codes) → libellé FR neutre. Code inconnu → affiché brut (défensif).
export const FAILURE_CODE_LABELS: Record<string, string> = {
  AUTH_AZ_FAILED: "Échec d'authentification Azure",
  KEYVAULT_ACCESS_FAILED: "Échec d'accès au coffre de clés (certificat / secret)",
  AUTH_GRAPH_FAILED: "Échec d'authentification Microsoft Graph (certificat / secret)",
  APP_NOT_AUTHORIZED: "Application non autorisée dans l'organisation (consentement requis)",
  ENTRA_PREMIUM_MISSING: "Entra ID P1/P2 absent de l'organisation",
  PERMISSIONS_MISSING: 'Permissions Microsoft Graph manquantes',
  CONFIG_INVALID: "Configuration d'audit invalide",
  UNEXPECTED: 'Erreur inattendue',
}

export function failureCodeLabel(code: string): string {
  return FAILURE_CODE_LABELS[code] ?? code
}

// Indice de remédiation opérateur par code (affiché sous le motif). Absent → rien.
export const FAILURE_CODE_HINTS: Record<string, string> = {
  APP_NOT_AUTHORIZED: "Relancez l'onboarding / le consentement de l'application dans l'organisation du client.",
  AUTH_GRAPH_FAILED: "Vérifiez le certificat ou le secret de l'application cliente.",
  KEYVAULT_ACCESS_FAILED: "Vérifiez le certificat/secret dans le coffre et les droits d'accès.",
  PERMISSIONS_MISSING: 'Accordez les permissions Microsoft Graph requises, puis reconsentez.',
  ENTRA_PREMIUM_MISSING: 'Cette organisation nécessite une licence Entra ID P1 ou P2.',
  CONFIG_INVALID: "Vérifiez les paramètres d'audit du client.",
}

export function failureCodeHint(code: string): string | undefined {
  return FAILURE_CODE_HINTS[code]
}

// §6.3 — catalogue warnings → libellé FR. Code inconnu → affiché brut (défensif).
export const WARNING_LABELS: Record<string, string> = {
  TENANT_NOT_PROVISIONED: 'Client non provisionné (tarification dégradée)',
  PERMISSIONS_OPTIONAL_MISSING: 'Permissions optionnelles manquantes',
  PRICE_UNRESOLVED: 'Tarif non résolu pour certaines licences',
  OVERRIDES_MISSING: 'Aucune remise / surcharge tarifaire définie',
  USAGE_REPORT_PARTIAL: "Rapport d'usage partiel",
  REPORTS_ANONYMIZED: "Rapports d'usage anonymisés (analyse par utilisateur indisponible)",
  SCENARIO_PRICING_DEGRADED: 'Tarification de scénario dégradée',
  GROUPS_COLLECTION_FAILED: 'Échec de collecte des groupes',
  // Contrat d'exécution 1.12 (spec 151) — preflight seulement ; le preflight reste réussi.
  LICENCE_INVENTORY_FAILED: 'Liste des licences du client non lue',
}

export function warningLabel(code: string): string {
  return WARNING_LABELS[code] ?? code
}

// Action recommandée par code warning (affichée sous le libellé — bandeau rapport, pill Portefeuille,
// volet de suivi). Absent → rien. Spec 082 : `REPORTS_ANONYMIZED` porté aussi par le snapshot (durable).
export const WARNING_HINTS: Record<string, string> = {
  REPORTS_ANONYMIZED:
    "Désactivez « Afficher les noms masqués » dans les paramètres des rapports du centre d'administration Microsoft 365, puis relancez l'audit pour évaluer les downgrades fondés sur l'usage.",
  LICENCE_INVENTORY_FAILED:
    "Le client est bien validé. Pour saisir ses surcharges avant le premier audit, relancez un preflight : il relira la liste de ses licences.",
}

export function warningHint(code: string): string | undefined {
  return WARNING_HINTS[code]
}

// Place-holder de jalon « à venir » (non encore émis) pour le squelette pré-affiché.
export interface SkeletonJalon extends CanonicalJalon {
  status: MilestoneStatus | 'pending'
}
